/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function UploadTextTracks() {
  return (
    <>
      <Meta
        title="Working with Text Track Uploads"
        description="Learn about the different kinds of text tracks and how to add them to videos through the Vimeo API."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Text Track Uploads</Path.Current>
          </Path>
        }
        nav={{ page: 'upload-texttracks' }}
      >
        <Header>Working with Text Track Uploads</Header>

        <Paragraph size="2">
          <em>Text tracks</em> are text-based content that you associate with your videos to make
          them more accessible. There are five different kinds:
        </Paragraph>

        <ul>
          <li>
            <strong>Subtitles.</strong> Subtitles superimpose the spoken dialogue in the video as
            onscreen text.
          </li>
          <li>
            <strong>Captions.</strong> Captions incorporate subtitles as well as additional text
            that describes the action in the video.
          </li>
          <li>
            <strong>Chapters.</strong> Chapters mark off specific scenes or sections of your video,
            which can help with navigation.
          </li>

          <StyledNotice>
            <strong>NOTE:</strong> You can't currently add chapters to a video by uploading them
            through the API, but you can create them manually through our onsite tool. For more
            information, see our{' '}
            <Link
              variant="minimal"
              href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426231245585-Chapters"
            >
              Help Center
            </Link>
            .
          </StyledNotice>

          <li>
            <strong>Descriptions.</strong> Descriptions are similar to the action text in captions,
            but they give a much more detailed account of what's happening in the video. Assistive
            technology like screen readers look specifically for description tracks.
          </li>
          <li>
            <strong>Metadata.</strong> Metadata text tracks are for delivering any other text-based
            content at specific times during playback, typically for programming purposes. For
            example, if you include JSON in a metadata text track, it's possible for an application
            to receive the JSON data at the indicated time and use this data to make changes to the
            surrounding page.
          </li>
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> The{' '}
          <Link variant="minimal" href="/player/sdk">
            Vimeo player
          </Link>{' '}
          doesn't currently support descriptions or metadata tracks. However, for cases where you're
          using a different video player, you can still upload these kinds of text tracks to your
          Vimeo videos through the API.
        </StyledNotice>

        <Paragraph size="2">
          The text track itself is just a specially structured text file in WebVTT format, which
          looks something like this in the case of simple subtitles (and very similar to this for
          the other kinds of text tracks):
        </Paragraph>

        <CodeBlock language="text">{`WEBVTT

00:01.000 --> 00:03.000
Now is the winter of our discontent.

00:04.000 --> 00:06.000
— Really?
— Really.

00:08.000 --> 00:09.000
Bummer.`}</CodeBlock>

        <Paragraph size="2">
          The time codes, or <em>cues</em>, indicate when (and for how long) to display each
          subtitle. More sophisticated WebVTT markup enables you to control the positioning and
          appearance of the text, indicate who's speaking, and so on. For complete information on
          the WebVTT format, see the{' '}
          <Link variant="minimal" href="https://www.w3.org/TR/webvtt1/">
            W3C specification
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          The Vimeo API enables you to upload text tracks to a video in multiple languages and
          indicate which text track should be active for any particular language. We show you how
          it's done in this guide.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A video on Vimeo.</strong> Haven't uploaded your video yet? See{' '}
            <Link variant="minimal" href="/api/upload/videos">
              Working with Video Uploads
            </Link>{' '}
            to learn how.
          </li>
          <li>
            <strong>A text track.</strong> Use WebVTT format, which has the VTT file extension.
          </li>
          <li>
            <strong>An API application.</strong> Not sure why you need this? See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>
            .
          </li>
          <li>
            <strong>
              An access token with the{' '}
              <code>
                <b>upload</b>
              </code>{' '}
              and{' '}
              <code>
                <b>edit</b>
              </code>{' '}
              scopes.
            </strong>{' '}
            See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>{' '}
            for more information.
          </li>
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> You might also consider using our official libraries for PHP,
          Python, Node.js, and a dozen other programming languages. The libraries help to simplify
          complicated coding by handling all the HTTP requests on your behalf. For more information,
          see our{' '}
          <Link variant="minimal" href="/api/libraries">
            Libraries and SDKs
          </Link>{' '}
          page.
        </StyledNotice>

        <Header size="2" id="uploading-a-text-track">
          Uploading a text track
        </Header>
        <Paragraph size="2">
          To upload text tracks using the API, follow this general workflow:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="uploading-a-text-track"
          items={[
            'Get the URI of the text track.',
            'Get the upload link for the text track.',
            'Upload the text track.',
            'Set the text track as active.',
          ]}
        />

        <Paragraph size="2">
          For details, endpoints, headers, and parameters, look no further than the following
          sections.
        </Paragraph>

        <FancyList anchorPrefix="uploading-a-text-track">
          <FancyListItem>
            <Header size="3">Get the URI of the text track</Header>
            <Paragraph size="2">
              Your first stop is the video that's getting the text track. The URI for the text track
              appears in the{' '}
              <code>
                <b>metadata.connections.texttracks.uri</b>
              </code>{' '}
              field of the video's representation. You can retrieve the complete representation from
              the API by sending an authenticated GET request to <code>/videos/{`{video_id}`}</code>
              :
            </Paragraph>

            <CodeBlock endpoint={{ method: 'get', resource: '/videos/{video_id}' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              for the headers to include.
            </Paragraph>

            <GuideTable
              tableNumber={1}
              description={
                <span>
                  Headers for GET to <code>/videos/{`{video_id}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <code>bearer {`{access_token}`}</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> Don't omit the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header in this request (and all subsequent requests in this guide), and always be sure
              to set it to the given value. This ensures that the API handles your response
              according to version 3.4. For more information, see{' '}
              <Link variant="minimal" href="/api/common-formats#using-the-accept-header">
                Using Common Formats and Parameters
              </Link>
              .
            </StyledNotice>

            <Paragraph size="2">
              Search the response for the{' '}
              <code>
                <b>metadata.connections.texttracks.uri</b>
              </code>{' '}
              field. You'll use the value of this field in Step 2.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Get the upload link for the text track</Header>
            <Paragraph size="2">
              To get the upload link for the text track, you must first create the text track's
              resource, which is like a container to hold the track. All you need is the URI from
              Step 1 and the following authenticated POST request:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'post', resource: '{texttracks_uri}' }} />

            <Paragraph size="2">
              The headers appear in{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={2}
              description={
                <span>
                  Headers for POST or PATCH to <code>{`{texttracks_uri}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <code>bearer {`{access_token}`}</code>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>type</b>
              </code>{' '}
              parameter to <code>subtitles</code>, <code>captions</code>, <code>descriptions</code>,
              or <code>metadata</code>, depending on the type of text track that you plan to upload
              — remember, you can't upload chapters through the API — and set the{' '}
              <code>
                <b>language</b>
              </code>{' '}
              parameter to indicate the language of the text track. The{' '}
              <code>
                <b>name</b>
              </code>{' '}
              parameter, which is optional, enables you to label the text track for ease of
              identification. This parameter comes in handy if you upload more than one text track
              for the same language.
            </Paragraph>

            <Paragraph size="2">Here's what the JSON looks like:</Paragraph>

            <CodeBlock language="json">
              {{
                type: '{type}',
                language: '{language_code}',
                name: '{name}',
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> If you need a complete list of valid{' '}
              <code>
                <b>language_code</b>
              </code>{' '}
              strings, send an authenticated GET request to{' '}
              <code>/languages?filter=texttracks</code>.
            </StyledNotice>

            <Paragraph size="2">
              The HTTP status comes back as 201 Created, meaning that the resource is good to go. Be
              sure to check the value of the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field in the response from the API; this is the URL where you'll upload the text track
              in the next step.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Upload the text track</Header>
            <Paragraph size="2">
              To upload the text track, take the value of the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field from Step 2, and make a PUT request to this location:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'put', resource: '{link}', httpPrefix: false }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              for the headers.
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description={
                <span>
                  Headers for PUT to <code>{`{link}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              In the body of the request, include the contents of the text track in WebVTT format.
              If you're using an authoring tool to generate the text track, open the VTT file in a
              text editor, copy the entire contents of the file, and paste it into the request body.
            </Paragraph>

            <Paragraph size="2">
              Assuming that all goes well, the API returns the HTTP 200 status code. The text track
              is now associated with the video, and it's accessible from the{' '}
              <code>/videos/{`{video_id}`}/texttracks/</code> and{' '}
              <code>
                /videos/{`{video_id}`}/texttracks/{`{texttrack_id}`}
              </code>{' '}
              endpoints; see our{' '}
              <Link variant="minimal" href="/api/reference/videos">
                API Reference
              </Link>{' '}
              for complete details.
            </Paragraph>

            <Paragraph size="2">
              The text track is also accessible from the player as long as it's the active track for
              that particular language. How do you make it the active track? We're glad you asked.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Set the text track as active</Header>
            <Paragraph size="2">
              To set the uploaded text track as <em>active,</em> or as the one that the player
              displays for this language, make an authorized PATCH request to the URI from Step 1:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'patch', resource: '{uri}' }} />

            <Paragraph size="2">
              Borrow the headers from{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>
              . You need{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>bearer {`{token}`}</code>,{' '}
              <code>
                <b>Content-Type</b>
              </code>{' '}
              as <code>application/json</code>, and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              The magic happens in the body of the request, where you send the{' '}
              <code>
                <b>active</b>
              </code>{' '}
              parameter in the affirmative:
            </Paragraph>

            <CodeBlock language="json">{{ active: true }}</CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> You can upload multiple text tracks to the same video, but only
              one text track per language can be active.
            </StyledNotice>

            <Paragraph size="2">
              HTTP status 200 OK tells you that the text track is active.
            </Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="changing-name-or-active-status">
          Changing the name or active status of a text track
        </Header>
        <Paragraph size="2">
          To change the name or active status of a text track using the API, here's what you do:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="changing-name-or-active-status"
          items={['Get the URI of the text track.', 'Set the new name or active status (or both).']}
        />

        <Paragraph size="2">The details appear in the following sections.</Paragraph>

        <FancyList anchorPrefix="changing-name-or-active-status">
          <FancyListItem>
            <Header size="3">Get the URI of the text track</Header>
            <Paragraph size="2">
              Send an authenticated GET request to <code>/videos/{`{video_id}`}/texttracks</code>:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'get', resource: '/videos/{video_id}/texttracks' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              for the headers:{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>bearer {`{access_token}`}</code> and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              A representation of all the video's text tracks comes back from the API. In the
              response, find the desired text track's{' '}
              <code>
                <b>data.uri</b>
              </code>{' '}
              field, and copy down this value for the next step. If the video has multiple text
              tracks, each one gets its own{' '}
              <code>
                <b>data.uri</b>
              </code>{' '}
              field, so be careful about choosing the right one.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Set the new name or active status (or both)</Header>
            <Paragraph size="2">
              The name and active status of the text track are just parameter values:{' '}
              <code>
                <b>name</b>
              </code>{' '}
              and{' '}
              <code>
                <b>active</b>
              </code>
              , respectively. So you can PATCH the new values to the URI from Step 1 with an
              authorized request:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'patch', resource: '{texttracks_uri}' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>{' '}
              for the headers:{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>bearer {`{access_token}`}</code>,{' '}
              <code>
                <b>Content-Type</b>
              </code>{' '}
              as <code>application/json</code>, and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              Send the parameters and their new values in the body of the request:
            </Paragraph>

            <CodeBlock language="json">
              {{
                name: '{new_name}',
                active: false,
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> If necessary, you can also change the type or language of the
              text track by including the{' '}
              <code>
                <b>type</b>
              </code>{' '}
              or{' '}
              <code>
                <b>language</b>
              </code>{' '}
              parameters in this request.
            </StyledNotice>

            <Paragraph size="2">
              The HTTP status of 200 OK indicates that your changes have been made.
            </Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="handling-errors">
          Handling errors
        </Header>
        <Paragraph size="2">
          Everything's fun until you get an error. Here's how to deal with the most common mistakes,
          disconnects, flubs, and boo-boos that uploaders of text tracks might encounter.
        </Paragraph>

        <Paragraph size="2">
          There are other things that can go wrong, too. So if we don't address your particular
          trouble in the following sections, have a look at our{' '}
          <Link variant="minimal" href="/api/reference/videos#create_text_track">
            API Reference
          </Link>
          .
        </Paragraph>

        <Header size="3" id="http-status-code-4xx">
          HTTP status code 4XX
        </Header>
        <Paragraph size="2">
          If the API returns an HTTP status code in the 400s, see{' '}
          <Link variant="minimal" href="#table-4">
            Table 4
          </Link>{' '}
          for the likely causes.
        </Paragraph>

        <GuideTable
          tableNumber={4}
          description="Probable causes for HTTP status code 4XX"
          headers={['HTTP status code', 'Probable cause']}
          rows={[
            [
              '400 Invalid Caption File',
              "The API doesn't recognize the format of the caption file.",
            ],
            [
              '403 Forbidden',
              "You're attempting to send data to a bad or expired link. Generate a new link, and try again.",
            ],
            [
              '405 Method Not Allowed',
              <span>
                You're using an unsupported HTTP method on the specified endpoint (like trying to
                PATCH the text track's active status to an endpoint other than{' '}
                <code>https://api.vimeo.com{`{texttracks_uri}`})</code>.
              </span>,
            ],
          ]}
        />

        <Header size="3" id="http-status-code-5xx">
          HTTP status code 5XX
        </Header>
        <Paragraph size="2">
          An HTTP status code in the 500s, like 500 Internal Server Error, means that there's a
          problem with the server. Give us a couple minutes to get our act together, and try your
          request again.
        </Paragraph>

        <Paragraph size="2">
          If you keep getting the same HTTP status code after repeated attempts,{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            let us know
          </Link>
          .
        </Paragraph>

        <Header size="3" id="error-code-2204-parameter-problem">
          Error code 2204: parameter problem
        </Header>
        <Paragraph size="2">
          Error code 2204 with HTTP status 400 Bad Request indicates that the API doesn't recognize
          one or more of your parameters in the header or the body of your request. Each bad
          parameter receives its own error code in the{' '}
          <code>
            <b>invalid_parameters</b>
          </code>{' '}
          block:
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 400 Bad Request', 'Content-Type: application/json']}
        >
          {{
            invalid_parameters: [
              {
                field: 'type',
                error_code: 2112,
                error: 'Something strange occurred. Please try again.',
                developer_message:
                  'You have provided an unrecognized type value. Valid types are `captions`, `subtitles`, `descriptions`, `chapters`, and `metadata`.',
              },
            ],
            error:
              "The application has received an invalid parameter. Please get in touch with the app's creator.",
            link: null,
            developer_message:
              "The parameters passed to this API endpoint didn't pass Vimeo's validation. Please check the `invalid_parameters` list for more information.",
            error_code: 2204,
          }}
        </CodeBlock>

        <Paragraph size="2">
          Common parameter errors appear in{' '}
          <Link variant="minimal" href="#table-5">
            Table 5
          </Link>
          .
        </Paragraph>

        <GuideTable
          tableNumber={5}
          description="Error codes for bad parameters"
          headers={['Error code', 'Description']}
          rows={[
            [
              '2112',
              <span>
                You've supplied an unrecognized text track type. Check the value of the{' '}
                <code>
                  <b>type</b>
                </code>{' '}
                parameter, and make sure that it's <code>subtitles</code>, <code>captions</code>,{' '}
                <code>descriptions</code>, or <code>metadata</code>. If you specify{' '}
                <code>chapters</code>, you won't get an error, but since you can't upload usable
                chapters through the API, there's no reason to send this type.
              </span>,
            ],
            [
              '2235',
              <span>
                We don't recognize the language code that you've specified. Check the value of the{' '}
                <code>
                  <b>language</b>
                </code>{' '}
                parameter.
              </span>,
            ],
          ]}
        />

        <Header size="3" id="error-code-2205-bad-body-format">
          Error code 2205: bad body format
        </Header>
        <Paragraph size="2">
          If you receive a 2205 error and HTTP status 400 Bad Request, the API is having trouble
          with the formatting of your request body:
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 400 Bad Request', 'Content-Type: application/json']}
        >
          {{
            error:
              'You have provided an invalid parameter. Please contact developer of this application.',
            link: null,
            developer_message:
              "The body of this HTTP request isn't formatted properly. Please check the `Content-Type` header and raw body.",
            error_code: 2205,
          }}
        </CodeBlock>

        <Paragraph size="2">
          Make sure that you format the body content properly and that the value of the{' '}
          <code>
            <b>Content-Type</b>
          </code>{' '}
          header is accurate.
        </Paragraph>

        <Header size="3" id="error-code-8003-unrecognized-access-token">
          Error code 8003: unrecognized access token
        </Header>
        <Paragraph size="2">
          An 8003 error with the HTTP status code 401 Authorization Required means that the API
          can't account for your access token:
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 401 Authorization Required', 'Content-Type: application/json']}
        >
          {{
            error: "Something strange occurred. Please get in touch with the app's creator.",
            link: null,
            developer_message: "The app didn't receive the user's credentials.",
            error_code: 8003,
          }}
        </CodeBlock>

        <Paragraph size="2">
          Make sure that you've supplied the correct token string. If it still doesn't work, see our{' '}
          <Link variant="minimal" href="/api/authentication">
            authentication documentation
          </Link>
          .
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          You've showered so much attention on glorious text that the image files in your life are
          feeling left out. Show them just how much you care by turning them into video thumbnails.
          Or make them wait a little longer while you pore over our{' '}
          <Link variant="minimal" href="/api/reference">
            API Reference
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          If you have any questions that we've neglected to answer, too bad! (No, seriously,{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            contact us
          </Link>
          .)
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
